import React from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Square, Activity, Wifi, Smartphone, Monitor, MessageCircle, Clock, CalendarDays } from 'lucide-react';
import clsx from 'clsx';

type Platform = 'whatsapp' | 'signal';

interface TrackerData {
    rtt: number;
    avg: number;
    median: number;
    threshold: number;
    state: string;
    timestamp: number;
}

interface DeviceInfo {
    jid: string;
    deviceId?: number;
    kind?: 'phone' | 'companion';
    state: string;
    rtt: number;
    avg: number;
}

interface SessionEntry {
    start: number;
    end: number;
}

// Format a millisecond duration as e.g. "7m 12s" or "1h 3m"
function formatDuration(ms: number): string {
    const totalSec = Math.max(0, Math.round(ms / 1000));
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
}

const timeFmt = (t: number) => new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
const dateFmt = (t: number) => new Date(t).toLocaleDateString([], { month: 'short', day: 'numeric' });

// Rolling one-month analytics window.
const ANALYTICS_DAYS = 30;
const DAY_MS = 24 * 60 * 60 * 1000;

interface DayBucket {
    key: string;      // stable key for the calendar day
    label: string;    // short axis label, e.g. "8/22"
    ms: number;       // online time that day, in ms
}

interface Analytics {
    totalMs: number;          // total online time across the window
    sessionCount: number;     // sessions that touched the window
    activeDays: number;       // days with any online time
    avgPerActiveDayMs: number;
    busiestHour: number | null; // hour-of-day (0-23) with the most online time
    daily: DayBucket[];       // one entry per day, oldest -> newest
}

// Format ms as a compact human duration for stat tiles, e.g. "12h 30m" / "45m".
function formatShort(ms: number): string {
    const totalMin = Math.round(ms / 60000);
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    if (h > 0) return m > 0 ? `${h}h ${m}m` : `${h}h`;
    return `${m}m`;
}

function formatHourRange(h: number): string {
    const fmt = (n: number) => {
        const period = n < 12 ? 'AM' : 'PM';
        const hr = n % 12 === 0 ? 12 : n % 12;
        return `${hr} ${period}`;
    };
    return `${fmt(h)}–${fmt((h + 1) % 24)}`;
}

// Compute one-month analytics from the recorded online sessions. Handles
// sessions that span midnight or multiple hours by splitting each on hour
// boundaries and crediting the time to the correct calendar day and hour.
function computeAnalytics(sessions: SessionEntry[], now: number): Analytics {
    const windowStart = now - ANALYTICS_DAYS * DAY_MS;

    // Prepare the ordered list of calendar days in the window (local time).
    const daily: DayBucket[] = [];
    const dayIndex = new Map<string, number>();
    const startOfToday = new Date(now);
    startOfToday.setHours(0, 0, 0, 0);
    for (let i = ANALYTICS_DAYS - 1; i >= 0; i--) {
        const d = new Date(startOfToday.getTime() - i * DAY_MS);
        const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
        dayIndex.set(key, daily.length);
        daily.push({ key, label: `${d.getMonth() + 1}/${d.getDate()}`, ms: 0 });
    }

    const hourMs = new Array(24).fill(0);
    let totalMs = 0;
    let sessionCount = 0;

    for (const s of sessions) {
        const start = Math.max(s.start, windowStart);
        const end = Math.min(s.end, now);
        if (end <= start) continue;
        sessionCount++;
        totalMs += end - start;

        // Walk the interval hour-by-hour so each slice lands in the right day
        // and hour-of-day bucket.
        let cursor = start;
        while (cursor < end) {
            const d = new Date(cursor);
            const nextHour = new Date(d);
            nextHour.setMinutes(60, 0, 0); // advance to the next :00
            const segEnd = Math.min(end, nextHour.getTime());
            const segMs = segEnd - cursor;

            hourMs[d.getHours()] += segMs;
            const dayKey = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
            const idx = dayIndex.get(dayKey);
            if (idx !== undefined) daily[idx].ms += segMs;

            cursor = segEnd;
        }
    }

    const activeDays = daily.filter(d => d.ms > 0).length;
    let busiestHour: number | null = null;
    let busiestMs = 0;
    for (let h = 0; h < 24; h++) {
        if (hourMs[h] > busiestMs) { busiestMs = hourMs[h]; busiestHour = h; }
    }

    return {
        totalMs,
        sessionCount,
        activeDays,
        avgPerActiveDayMs: activeDays > 0 ? totalMs / activeDays : 0,
        busiestHour,
        daily,
    };
}

interface ContactCardProps {
    jid: string;
    displayNumber: string;
    data: TrackerData[];
    devices: DeviceInfo[];
    deviceCount: number;
    presence: string | null;
    profilePic: string | null;
    onRemove: () => void;
    privacyMode?: boolean;
    platform?: Platform;
    sessions?: SessionEntry[];
    // Opt-in: treat RTT reachability as "Online" when WhatsApp presence is
    // unavailable. Off by default because reachability over-reports (a pocketed
    // phone or an always-on Web/Desktop session ACKs probes 24/7).
    treatReachableAsOnline?: boolean;
}

export function ContactCard({
    jid,
    displayNumber,
    data,
    devices,
    deviceCount,
    presence,
    profilePic,
    onRemove,
    privacyMode = false,
    platform = 'whatsapp',
    sessions = [],
    treatReachableAsOnline = false
}: ContactCardProps) {
    const lastData = data[data.length - 1];

    // Headline status. Two independent signals feed this:
    //   1. RTT reachability — a device ACKs our silent probes. This proves the
    //      device is CONNECTED TO THE NETWORK, not that the person is using the
    //      app. An always-on WhatsApp Web/Desktop session (a companion device)
    //      is reachable 24/7, so "any device online" over-reports the person.
    //   2. WhatsApp presence (available/composing) — only sent while the app is
    //      in the FOREGROUND on the phone. This is the real "active now" signal.
    //
    // So we drive the badge off the PHONE (device id 0) and the presence, and
    // surface companion-only reachability as a distinct, weaker state:
    //   Active   → presence says foreground (green)
    //   Reachable→ phone answers probes but not confirmably in-app (amber)
    //   Linked   → only a Web/Desktop session is reachable (amber, muted)
    //   Offline  → nothing answers (red)
    const phoneDevice = devices.find(d => d.kind === 'phone');
    // Fallback for older data with no `kind`: treat the first device as phone.
    const phoneState = (phoneDevice ?? devices[0])?.state ?? '';
    const phoneOnline = phoneState.includes('Online') || phoneState.includes('Calibrating') || phoneState === 'Standby';
    const companionOnline = devices.some(d => d.kind === 'companion' && d.state.includes('Online'));
    const presenceActive = presence === 'available' || presence === 'composing' || presence === 'recording';
    const anyOnline = devices.some(d => d.state.includes('Online') || d.state === 'Standby' || d.state.includes('Calibrating'));

    // "Unreachable" = the server has been probing but WhatsApp never delivers to
    // this number (blocked us / privacy-restricted / long-term offline), so no
    // receipt ever comes back and no RTT can exist. Reported as a dedicated
    // device state so it's told apart from a normal Offline contact.
    const unreachable = devices.some(d => d.state === 'UNREACHABLE');

    type StatusTone = 'active' | 'reachable' | 'offline' | 'unreachable' | 'unknown';
    let currentStatus: string;
    let statusTone: StatusTone;
    if (unreachable) {
        currentStatus = 'Unreachable';
        statusTone = 'unreachable';
    } else if (devices.length === 0) {
        currentStatus = 'Unknown';
        statusTone = 'unknown';
    } else if (presenceActive) {
        currentStatus = presence === 'composing' ? 'Active · typing' : 'Active';
        statusTone = 'active';
    } else if (phoneOnline && treatReachableAsOnline) {
        // Opt-in: presence is unavailable (often privacy-blocked) but the phone
        // is answering probes. Surface it as Online instead of the weaker
        // Reachable. Note the over-reporting caveat in the prop's doc comment.
        currentStatus = 'Online';
        statusTone = 'active';
    } else if (phoneOnline) {
        currentStatus = 'Reachable';
        statusTone = 'reachable';
    } else if (companionOnline && treatReachableAsOnline) {
        currentStatus = 'Online';
        statusTone = 'active';
    } else if (companionOnline) {
        currentStatus = 'Linked device online';
        statusTone = 'reachable';
    } else {
        currentStatus = 'Offline';
        statusTone = 'offline';
    }
    // The activity log records WhatsApp presence (app open), not reachability,
    // so the "ongoing" session marker must follow the same signal. Signal has no
    // presence, so it keeps using device reachability.
    const isLiveNow = platform === 'whatsapp' ? presenceActive : anyOnline;

    // Blur phone number in privacy mode
    const blurredNumber = privacyMode ? displayNumber.replace(/\d/g, '•') : displayNumber;

    // One-month activity analytics, derived from the server-recorded sessions.
    const now = Date.now();
    const analytics = computeAnalytics(sessions, now);
    const windowStart = now - ANALYTICS_DAYS * DAY_MS;
    // Sessions that fall within the last 30 days, newest first, for the log list.
    const recentSessions = sessions.filter(s => s.end >= windowStart);

    return (
        <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl shadow-lg border border-gray-200 overflow-hidden">
            {/* Header with Stop Button */}
            <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <span className={clsx(
                        "px-2 py-1 rounded text-xs font-medium flex items-center gap-1",
                        platform === 'whatsapp' ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"
                    )}>
                        <MessageCircle size={12} />
                        {platform === 'whatsapp' ? 'WhatsApp' : 'Signal'}
                    </span>
                    <h3 className="text-lg font-semibold text-gray-900">{blurredNumber}</h3>
                </div>
                <button
                    onClick={onRemove}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2 font-medium transition-colors text-sm"
                >
                    <Square size={16} /> Stop
                </button>
            </div>

            <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Status Card */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 flex flex-col items-center text-center">
                        <div className="relative mb-4">
                            <div className="w-32 h-32 rounded-full overflow-hidden bg-gray-100 border-4 border-white shadow-md">
                                {profilePic ? (
                                    <img
                                        src={profilePic}
                                        alt="Profile"
                                        className={clsx(
                                            "w-full h-full object-cover transition-all duration-200",
                                            privacyMode && "blur-xl scale-110"
                                        )}
                                        style={privacyMode ? {
                                            filter: 'blur(16px) contrast(0.8)',
                                        } : {}}
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                                        No Image
                                    </div>
                                )}
                            </div>
                            <div className={clsx(
                                "absolute bottom-2 right-2 w-6 h-6 rounded-full border-2 border-white",
                                statusTone === 'active' ? "bg-green-500" :
                                    statusTone === 'reachable' ? "bg-amber-500" :
                                        statusTone === 'offline' ? "bg-red-500" :
                                            statusTone === 'unreachable' ? "bg-red-600" : "bg-gray-400"
                            )} />
                        </div>

                        <h4 className="text-xl font-bold text-gray-900 mb-1">{blurredNumber}</h4>

                        <div className="flex items-center gap-2 mb-4">
                            <span className={clsx(
                                "px-3 py-1 rounded-full text-sm font-medium",
                                statusTone === 'active' ? "bg-green-100 text-green-700" :
                                    statusTone === 'reachable' ? "bg-amber-100 text-amber-700" :
                                        statusTone === 'offline' ? "bg-red-100 text-red-700" :
                                            statusTone === 'unreachable' ? "bg-red-100 text-red-800" : "bg-gray-100 text-gray-700"
                            )}>
                                {currentStatus}
                            </span>
                        </div>

                        <p className="text-[11px] leading-snug text-gray-400 -mt-2 mb-4 px-2">
                            {statusTone === 'active' && presenceActive
                                ? 'App is open in the foreground right now.'
                                : statusTone === 'active'
                                    ? 'Device is answering probes. Shown as Online per your setting; not confirmed in-app.'
                                : statusTone === 'reachable'
                                    ? 'Device is connected to the network (may be in a pocket/background), not confirmably in the app.'
                                    : statusTone === 'unreachable'
                                        ? 'WhatsApp is not delivering probes to this number — it has likely blocked this account, restricted delivery, or has been offline for a long time. Activity can’t be tracked until it delivers again.'
                                    : statusTone === 'offline'
                                        ? 'No device responded to probes.'
                                        : 'Waiting for the first probe response…'}
                        </p>

                        <div className="w-full pt-4 border-t border-gray-100 space-y-2">
                            <div className="flex justify-between items-center text-sm text-gray-600">
                                <span className="flex items-center gap-1"><Wifi size={16} /> Official Status</span>
                                <span className="font-medium">{presence || 'Unknown'}</span>
                            </div>
                            <div className="flex justify-between items-center text-sm text-gray-600">
                                <span className="flex items-center gap-1"><Smartphone size={16} /> Devices</span>
                                <span className="font-medium">{deviceCount || 0}</span>
                            </div>
                        </div>

                        {/* Device List */}
                        {devices.length > 0 && (
                            <div className="w-full pt-4 border-t border-gray-100 mt-4">
                                <h5 className="text-xs font-semibold text-gray-500 uppercase mb-2">Device States</h5>
                                <div className="space-y-1">
                                    {[...devices]
                                        .sort((a, b) => (a.deviceId ?? 0) - (b.deviceId ?? 0))
                                        .map((device, idx) => {
                                        const isPhone = (device.kind ?? (idx === 0 ? 'phone' : 'companion')) === 'phone';
                                        const label = isPhone ? 'Phone (primary)' : 'Web / Desktop';
                                        return (
                                        <div key={device.jid} className="flex items-center justify-between text-sm py-1">
                                            <div className="flex items-center gap-2">
                                                {isPhone
                                                    ? <Smartphone size={14} className="text-gray-400" />
                                                    : <Monitor size={14} className="text-gray-400" />}
                                                <span className="text-gray-600">{label}</span>
                                            </div>
                                            <span className={clsx(
                                                "px-2 py-0.5 rounded text-xs font-medium",
                                                device.state === 'OFFLINE' ? "bg-red-100 text-red-700" :
                                                    device.state.includes('Online') ? "bg-amber-100 text-amber-700" :
                                                        device.state === 'Standby' ? "bg-yellow-100 text-yellow-700" : "bg-gray-100 text-gray-700"
                                            )}>
                                                {/* An ACK only proves the device is connected (WhatsApp
                                                    delivers in the background too), not that the app is
                                                    open — so don't call it "Online". */}
                                                {device.state.includes('Online') ? 'Reachable' : device.state}
                                            </span>
                                        </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Metrics & Chart */}
                    <div className="md:col-span-2 space-y-6">
                        {/* Metrics Grid */}
                        <div className="grid grid-cols-3 gap-4">
                            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
                                <div className="text-sm text-gray-500 mb-1 flex items-center gap-1"><Activity size={16} /> Current Avg RTT</div>
                                <div className="text-2xl font-bold text-gray-900">{lastData?.avg.toFixed(0) || '-'} ms</div>
                            </div>
                            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
                                <div className="text-sm text-gray-500 mb-1">Median (50)</div>
                                <div className="text-2xl font-bold text-gray-900">{lastData?.median.toFixed(0) || '-'} ms</div>
                            </div>
                            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
                                <div className="text-sm text-gray-500 mb-1">Threshold</div>
                                <div className="text-2xl font-bold text-blue-600">{lastData?.threshold.toFixed(0) || '-'} ms</div>
                            </div>
                        </div>

                        {/* Chart */}
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 h-[300px]">
                            <h5 className="text-sm font-medium text-gray-500 mb-4">RTT History & Threshold</h5>
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={data}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                    <XAxis dataKey="timestamp" hide />
                                    <YAxis domain={['auto', 'auto']} />
                                    <Tooltip
                                        labelFormatter={(t: any) => new Date(Number(t)).toLocaleTimeString()}
                                        contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                                    />
                                    <Line type="monotone" dataKey="avg" stroke="#3b82f6" strokeWidth={2} dot={false} name="Avg RTT" isAnimationActive={false} />
                                    <Line type="step" dataKey="threshold" stroke="#ef4444" strokeDasharray="5 5" dot={false} name="Threshold" isAnimationActive={false} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>

                        {/* 30-Day Activity Analytics */}
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                            <div className="flex items-center justify-between mb-4">
                                <h5 className="text-sm font-medium text-gray-500 flex items-center gap-1">
                                    <CalendarDays size={16} /> Last 30 Days
                                </h5>
                                <span className="text-xs text-gray-400">
                                    {analytics.activeDays} active day{analytics.activeDays === 1 ? '' : 's'}
                                </span>
                            </div>

                            {/* Summary stat tiles */}
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                                <div className="bg-gray-50 rounded-lg p-3">
                                    <div className="text-xs text-gray-500 mb-1">Total online</div>
                                    <div className="text-lg font-bold text-gray-900">{formatShort(analytics.totalMs)}</div>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-3">
                                    <div className="text-xs text-gray-500 mb-1">Sessions</div>
                                    <div className="text-lg font-bold text-gray-900">{analytics.sessionCount}</div>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-3">
                                    <div className="text-xs text-gray-500 mb-1">Avg / active day</div>
                                    <div className="text-lg font-bold text-gray-900">{formatShort(analytics.avgPerActiveDayMs)}</div>
                                </div>
                                <div className="bg-gray-50 rounded-lg p-3">
                                    <div className="text-xs text-gray-500 mb-1">Busiest hour</div>
                                    <div className="text-lg font-bold text-gray-900">
                                        {analytics.busiestHour === null ? '—' : formatHourRange(analytics.busiestHour)}
                                    </div>
                                </div>
                            </div>

                            {/* Daily online-time bar chart */}
                            <div className="h-[180px]">
                                {analytics.totalMs === 0 ? (
                                    <div className="h-full flex items-center justify-center">
                                        <p className="text-sm text-gray-400">No online activity in the last 30 days.</p>
                                    </div>
                                ) : (
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={analytics.daily} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                            <XAxis
                                                dataKey="label"
                                                tick={{ fontSize: 10, fill: '#9ca3af' }}
                                                interval={4}
                                                tickLine={false}
                                                axisLine={{ stroke: '#e5e7eb' }}
                                            />
                                            <YAxis
                                                tickFormatter={(v: number) => `${Math.round(v / 3600000)}h`}
                                                tick={{ fontSize: 10, fill: '#9ca3af' }}
                                                tickLine={false}
                                                axisLine={false}
                                                width={40}
                                            />
                                            <Tooltip
                                                cursor={{ fill: 'rgba(59,130,246,0.06)' }}
                                                formatter={(v: any) => [formatShort(Number(v)), 'Online']}
                                                contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                                            />
                                            <Bar dataKey="ms" fill="#3b82f6" radius={[3, 3, 0, 0]} isAnimationActive={false} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                )}
                            </div>
                        </div>

                        {/* Activity Log — recorded online sessions (last 30 days) */}
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                            <div className="flex items-center justify-between mb-4">
                                <h5 className="text-sm font-medium text-gray-500 flex items-center gap-1">
                                    <Clock size={16} /> Activity Log · 30 days
                                </h5>
                                <span className="text-xs text-gray-400">{recentSessions.length} session{recentSessions.length === 1 ? '' : 's'}</span>
                            </div>
                            {recentSessions.length === 0 ? (
                                <p className="text-sm text-gray-400">No online activity recorded yet.</p>
                            ) : (
                                <div className="max-h-[220px] overflow-y-auto divide-y divide-gray-100">
                                    {[...recentSessions].reverse().map((session, idx) => {
                                        // The newest session is "ongoing" only while the contact
                                        // is actually Online right now. As soon as it drops
                                        // OFFLINE the session's end time is locked and shown.
                                        const active = idx === 0 && isLiveNow;
                                        const durationMs = session.end - session.start;
                                        return (
                                            <div key={`${session.start}-${session.end}`} className="flex items-center justify-between py-2 text-sm">
                                                <div className="flex items-center gap-2">
                                                    <span className={clsx(
                                                        "w-2 h-2 rounded-full",
                                                        active ? "bg-green-500 animate-pulse" : "bg-gray-300"
                                                    )} />
                                                    <span className="text-gray-700">
                                                        {dateFmt(session.start)} · {timeFmt(session.start)}
                                                        {' → '}
                                                        {active ? 'now' : timeFmt(session.end)}
                                                    </span>
                                                </div>
                                                <span className={clsx(
                                                    "px-2 py-0.5 rounded text-xs font-medium",
                                                    active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"
                                                )}>
                                                    {formatDuration(durationMs)}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
